import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { IconType, WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import {
  MinMaxStep,
  SectionChange,
  SectionModelType,
  SelectionModifiers,
  WorkoutSectionModeType,
} from '../types/section.type';
import {
  DEFAULT_WORKOUT_SECTION_MIN_MAX_STEP,
  MULTI_FIELD_WORKOUT_SECTION_MIN_MAX_STEP,
} from '../configs/sections-editor/workout-mode-initial-min-max-avr.config';
import {
  DEFAULT_WORKOUT_MODE_ICON,
  WORKOUT_MODE_ICONS,
} from '../configs/sections-editor/workout-mode-icons.config';
import {
  DEFAULT_WORKOUT_MODE_UNIT,
  WORKOUT_MODE_UNITS,
} from '../configs/sections-editor/workout-mode-units.config';
import {
  DEFAULT_SECTION_CADENCE,
  MIN_SECTION_DURATION_SECONDS,
  SECTION_CADENCE_MIN_MAX_STEP,
} from '../configs/sections-editor/default-section.config';
import { durationToSeconds, secondsToDuration } from '../utils/duration.utils';
import { CreateContentService } from './create-content.service';
import {
  DEFAULT_WORKOUT_MODE_FIELDS_MODE,
  WORKOUT_MODE_FIELDS,
} from '../configs/sections-editor/workout-mode-fields.config';
import { WORKOUT_MODE_LABELS } from '../configs/sections-editor/workout-mode-labels.config';

type WorkoutField = { type: WorkoutMode; key: string };

@Injectable({
  providedIn: 'root',
})
export class SectionsEditorService {
  private readonly createContentService: CreateContentService = inject(CreateContentService);
  public readonly workoutMode: WritableSignal<WorkoutMode | undefined> = signal(undefined);
  /** Type du contenu edite, fourni par l'editeur. */
  public readonly contentType: WritableSignal<'coaching' | 'workout' | undefined> =
    signal(undefined);

  /** Champ affiche en blocs : le premier champ du mode. */
  public readonly primaryField: Signal<WorkoutField> = computed(
    () => this.fieldsFor(this.workoutMode())[0],
  );

  /** Champ affiche en trait par-dessus les blocs : le deuxieme champ du mode, s'il existe. */
  public readonly secondaryField: Signal<WorkoutField | undefined> = computed(
    () => this.fieldsFor(this.workoutMode())[1],
  );

  /** Bornes du champ principal, celles des blocs et de l'axe de gauche. */
  private readonly minMaxStep: Signal<Readonly<MinMaxStep>> = computed(() => this.fieldBounds());

  /** Bornes du champ secondaire, celles du trait et de l'axe de droite. */
  public readonly secondaryMinMaxStep: Signal<Readonly<MinMaxStep> | undefined> = computed(() => {
    const field: WorkoutField | undefined = this.secondaryField();
    return field ? this.fieldBounds(field.type) : undefined;
  });

  public readonly min: Signal<number> = computed(() => this.minMaxStep().min);
  public readonly max: Signal<number> = computed(() => this.minMaxStep().max);
  public readonly step: Signal<number> = computed(() => this.minMaxStep().step);

  hoveredSectionId: WritableSignal<number | undefined> = signal(undefined);
  selectedSectionsIds: WritableSignal<number[]> = signal([]);
  /** Sections du workout, fournies par l'editeur via `bindSections`. */
  private readonly sectionsSource: WritableSignal<Signal<SectionModelType[]>> = signal(signal([]));
  /** Ids des sections dans l'ordre du workout, necessaire a la selection par plage (Shift). */
  private readonly sectionsOrder: Signal<number[]> = computed(() =>
    this.sectionsSource()().map((section: SectionModelType) => section.id),
  );
  /** Point de depart d'une selection par plage : dernier clic simple ou Ctrl/Cmd. */
  private selectionAnchorId: number | undefined;

  /**
   * La selection ne se deplace que d'un seul tenant : des qu'une section
   * selectionnee touche un bord, on ne peut plus aller de ce cote.
   */
  readonly canMoveSelectionLeft: Signal<boolean> = computed(() => {
    const selectedIds: number[] = this.selectedSectionsIds();
    return selectedIds.length > 0 && !selectedIds.includes(this.sectionsOrder()[0]);
  });
  readonly canMoveSelectionRight: Signal<boolean> = computed(() => {
    const selectedIds: number[] = this.selectedSectionsIds();
    return selectedIds.length > 0 && !selectedIds.includes(this.sectionsOrder().at(-1)!);
  });

  bindSections(sections: Signal<SectionModelType[]>): void {
    this.sectionsSource.set(sections);
  }

  bindSelection(selection: WritableSignal<number[]>): void {
    this.selectedSectionsIds = selection;
  }

  selectSection(id: number, { toggle, range }: SelectionModifiers): void {
    const anchorIndex: number =
      this.selectionAnchorId === undefined
        ? -1
        : this.sectionsOrder().indexOf(this.selectionAnchorId);

    if (range && anchorIndex !== -1) {
      const targetIndex: number = this.sectionsOrder().indexOf(id);
      const rangeIds: number[] = this.sectionsOrder().slice(
        Math.min(anchorIndex, targetIndex),
        Math.max(anchorIndex, targetIndex) + 1,
      );
      this.selectedSectionsIds.update((ids: number[]) =>
        toggle ? [...new Set([...ids, ...rangeIds])] : rangeIds,
      );
      return;
    }

    this.selectionAnchorId = id;
    if (toggle) {
      this.toggleSectionSelection(id);
      return;
    }
    const ids: number[] = this.selectedSectionsIds();
    this.selectedSectionsIds.set(ids.length === 1 && ids[0] === id ? [] : [id]);
  }

  toggleSectionSelection(id: number): void {
    this.selectedSectionsIds.update((ids) =>
      ids.includes(id) ? ids.filter((selectedId) => selectedId !== id) : [...ids, id],
    );
  }

  clearSectionSelection(): void {
    this.selectedSectionsIds.set([]);
    this.selectionAnchorId = undefined;
  }

  getWorkoutModeIcon(mode: WorkoutMode | undefined): IconType {
    return mode ? WORKOUT_MODE_ICONS[mode] : DEFAULT_WORKOUT_MODE_ICON;
  }

  getWorkoutModeUnit(mode: WorkoutMode | undefined): string {
    return mode ? WORKOUT_MODE_UNITS[mode] : DEFAULT_WORKOUT_MODE_UNIT;
  }

  /** Cadence optionnelle : dans tous les modes en coaching, en cycling seulement en workout. */
  canHaveCadence(mode: WorkoutMode | undefined): boolean {
    if (mode === undefined) return false;
    return (
      this.contentType() === 'coaching' || this.createContentService.workoutSport() === 'cycling'
    );
  }

  getWorkoutModeLabel(mode: WorkoutMode): string {
    return WORKOUT_MODE_LABELS[mode];
  }

  /**
   * Bornes du champ `type` dans le mode courant (le premier champ du mode si absent).
   * Un mode a plusieurs valeurs peut redefinir les bornes de ses champs ; sinon
   * chaque champ garde celles de son propre mode.
   */
  fieldBounds(type?: WorkoutMode): Readonly<MinMaxStep> {
    return this.fieldBoundsFor(this.workoutMode(), type);
  }

  fieldBoundsFor(mode: WorkoutMode | undefined, type?: WorkoutMode): Readonly<MinMaxStep> {
    if (!mode) return DEFAULT_WORKOUT_SECTION_MIN_MAX_STEP.ftp;
    const fieldType: WorkoutMode = type ?? this.fieldsFor(mode)[0].type;
    return (
      MULTI_FIELD_WORKOUT_SECTION_MIN_MAX_STEP[mode]?.[fieldType] ??
      DEFAULT_WORKOUT_SECTION_MIN_MAX_STEP[fieldType]
    );
  }

  private fieldsFor(mode: WorkoutMode | undefined): readonly WorkoutField[] {
    return WORKOUT_MODE_FIELDS[mode ?? DEFAULT_WORKOUT_MODE_FIELDS_MODE];
  }

  sectionDatas(
    section: SectionModelType,
    mode: WorkoutMode | undefined,
  ): { type: WorkoutMode; value: number }[] {
    const data: Record<string, number> = section.data as Record<string, number>;
    return this.fieldsFor(mode).map(({ type, key }) => ({ type, value: data[key] }));
  }

  updateWorkoutSectionModeType(
    section: SectionModelType,
    newData: number,
    mode: WorkoutMode | undefined,
    type?: WorkoutMode,
  ): WorkoutSectionModeType {
    const fields: readonly { type: WorkoutMode; key: string }[] = this.fieldsFor(mode);
    const updatedKey: string = (fields.find((f) => f.type === type) ?? fields[0]).key;
    const data: Record<string, number> = section.data as Record<string, number>;
    return Object.fromEntries(
      fields.map(({ key }) => [key, key === updatedKey ? newData : data[key]]),
    ) as WorkoutSectionModeType;
  }

  /**
   * Retourne une copie de `section` decalee de `change.delta`, bornee a
   * `[min, max]` pour la donnee et la cadence, et a `MIN_SECTION_DURATION_SECONDS` pour la duree.
   */
  applyChange(
    section: SectionModelType,
    change: SectionChange,
    mode: WorkoutMode | undefined,
  ): SectionModelType {
    if (change.field === 'duration') {
      const seconds: number = durationToSeconds(section.duration) + change.delta;
      return {
        ...section,
        duration: secondsToDuration(Math.max(MIN_SECTION_DURATION_SECONDS, seconds)),
      };
    }
    if (change.field === 'cadence') {
      if (change.delta === undefined) return { ...section, cadence: undefined };
      const { min, max }: Readonly<MinMaxStep> = SECTION_CADENCE_MIN_MAX_STEP;
      const cadence: number = (section.cadence ?? DEFAULT_SECTION_CADENCE) + change.delta;
      return { ...section, cadence: Math.min(max, Math.max(min, cadence)) };
    }
    const current: number = this.sectionDataValue(section, mode, change.type);
    const { min, max }: Readonly<MinMaxStep> = this.fieldBounds(change.type);
    const value: number = Math.min(max, Math.max(min, current + change.delta));
    return {
      ...section,
      data: this.updateWorkoutSectionModeType(section, value, mode, change.type),
    };
  }

  /** Valeur du champ `type` de la section (le premier champ du mode si absent). */
  sectionDataValue(
    section: SectionModelType,
    mode: WorkoutMode | undefined,
    type?: WorkoutMode,
  ): number {
    const datas: { type: WorkoutMode; value: number }[] = this.sectionDatas(section, mode);
    return (datas.find((d) => d.type === type) ?? datas[0]).value;
  }
}
